import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { Router } from '@angular/router';

import { NvButton } from '../../../shared/ui/button/nv-button';
import { NvToastService } from '../../../shared/ui/toast/nv-toast';
import { NvDialogService } from '../../../shared/ui/dialog/nv-dialog.service';
import { PermissionService } from '../../../core/permissions/permission.service';
import {
  RewardPinResetDialog,
  RewardPinResetDialogData,
} from '../../../shared/components/reward-pin-reset-dialog/reward-pin-reset-dialog';
import { PendingRequestsService } from '../../../core/notifications/pending-requests.service';
import { TenancyService } from '../../../core/tenancy/tenancy.service';
import { OrdersStore } from '../../../core/stores/orders.store';
import { FloorStore } from '../../../core/stores/floor.store';
import { GuestRequest } from '../../../shared/models/guest-request.model';
import { REQUEST_REASON_LABEL } from '../../../shared/models/guest-request-labels';
import { FirestoreTimestampLike, toMillis } from '../../../shared/utils/timestamp.util';
import { formatMinorAsRupeesClient } from '../../../shared/utils/money.util';

const MAX_ROWS = 5;

/**
 * Concern 8/14 — the header bell had a live badge (PendingRequestsService.count)
 * but no `(click)` handler at all, which read as worse than disabled: it
 * looked live and did nothing. This is what it opens — the same two feeds
 * the requests-tasks-dock's tabs show (guest requests, orders awaiting
 * approval), condensed to a quick-glance list with a jump-to-full-list link
 * per section, rather than a second full copy of the dock's own escalation
 * UI.
 *
 * Its own lazily-mounted component (only constructed while the panel is
 * open) for the same reason header-search-results is: `FloorStore` isn't
 * already running app-wide the way `OrdersStore`/`PendingRequestsService`
 * are (via the always-mounted requests-tasks-dock), so this defers that
 * listener to whoever actually opens the bell.
 */
@Component({
  selector: 'nv-header-notifications-panel',
  imports: [NvButton],
  templateUrl: './header-notifications-panel.html',
  styleUrl: './header-notifications-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderNotificationsPanel {
  private readonly router = inject(Router);
  private readonly pendingRequestsService = inject(PendingRequestsService);
  private readonly tenancy = inject(TenancyService);
  private readonly ordersStore = inject(OrdersStore);
  private readonly floor = inject(FloorStore);
  private readonly toasts = inject(NvToastService);
  private readonly dialog = inject(NvDialogService);
  private readonly permissions = inject(PermissionService);

  readonly closed = output<void>();

  readonly reasonLabel = REQUEST_REASON_LABEL;
  readonly formatPrice = formatMinorAsRupeesClient;

  private readonly tableNumbers = computed(() => {
    const map = new Map<string, number>();
    for (const table of this.floor.tables()) map.set(table.id, table.number);
    return map;
  });

  tableLabel(tableId: string): string {
    const number = this.tableNumbers().get(tableId);
    return number !== undefined ? `Table ${number}` : tableId;
  }

  /**
   * Round 7, §9 — a relative-age line ("4 min ago"), so a newest-first feed
   * doesn't read as ambiguous about which end is which — same wording shape
   * as request-alerts.ts's own waitingFor(), just phrased as a feed
   * timestamp rather than a queue wait.
   */
  ageLabel(createdAt: FirestoreTimestampLike): string {
    const at = toMillis(createdAt);
    if (!at) return 'just now';
    const seconds = Math.max(0, Math.floor((Date.now() - at) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  }

  /** Round 7, §9 — the bell is a feed (newest first), unlike the dock's own oldest-first work queue reading the same underlying data. */
  readonly requests = computed(() => this.pendingRequestsService.newestFirst().slice(0, MAX_ROWS));
  readonly totalRequests = this.pendingRequestsService.count;

  /** QA round 2, item I.1 — acknowledged-but-not-done requests, same reasoning as RequestAlerts.acknowledgedRequests: a separate feed so "Mark done" doesn't require widening the alarm-driving `pending`/`count` signals. */
  readonly acknowledgedRequests = computed(() =>
    [...this.pendingRequestsService.acknowledgedOpen()]
      .sort((a, b) => (toMillis(b.createdAt) ?? 0) - (toMillis(a.createdAt) ?? 0))
      .slice(0, MAX_ROWS)
  );
  readonly totalAcknowledgedRequests = computed(() => this.pendingRequestsService.acknowledgedOpen().length);

  readonly orders = computed(() =>
    [...this.ordersStore.pendingApproval()]
      .sort((a, b) => (toMillis(b.createdAt) ?? 0) - (toMillis(a.createdAt) ?? 0))
      .slice(0, MAX_ROWS)
  );
  readonly totalOrders = computed(() => this.ordersStore.pendingApproval().length);

  /** Stage 15 — guest "I paid by UPI" claims awaiting a manager's check. */
  readonly paymentClaims = this.pendingRequestsService.orderedPaymentClaims;
  readonly totalPaymentClaims = computed(() => this.paymentClaims().length);

  /** Midnight day-close summaries — tables auto-closed, money moved to dues, tables left open. */
  readonly dayCloseAlerts = this.pendingRequestsService.orderedDayCloseAlerts;
  readonly totalDayCloseAlerts = computed(() => this.dayCloseAlerts().length);
  readonly opsAlerts = this.pendingRequestsService.orderedOpsAlerts;
  readonly totalOpsAlerts = computed(() => this.opsAlerts().length);

  /** "Table 1, Table 4" — prefers the number the job recorded, falls back to the live floor lookup. */
  tableList(tables: { tableId: string; tableNumber: number | null }[]): string {
    return tables.map((t) => (t.tableNumber !== null ? `Table ${t.tableNumber}` : this.tableLabel(t.tableId))).join(', ');
  }

  viewReceivables(): void {
    void this.router.navigate(['/billing'], { queryParams: { view: 'receivables' } });
    this.closed.emit();
  }

  readonly isEmpty = computed(
    () =>
      this.totalRequests() === 0 &&
      this.totalAcknowledgedRequests() === 0 &&
      this.totalOrders() === 0 &&
      this.totalPaymentClaims() === 0 &&
      this.totalDayCloseAlerts() === 0 &&
      this.totalOpsAlerts() === 0
  );

  private readonly acknowledging = signal<ReadonlySet<string>>(new Set());

  isAcknowledging(requestId: string): boolean {
    return this.acknowledging().has(requestId);
  }

  async acknowledge(requestId: string): Promise<void> {
    this.acknowledging.update((ids) => new Set(ids).add(requestId));
    try {
      await this.pendingRequestsService.acknowledge(requestId);
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not acknowledge that.', 'Dismiss', { duration: 4000 });
    } finally {
      this.acknowledging.update((ids) => {
        const next = new Set(ids);
        next.delete(requestId);
        return next;
      });
    }
  }

  /** QA round 2, item I.1 — the lifecycle's last step, reachable from the header bell too. No alarm/sound: Done is staff clearing their own already-handled item. */
  async markDone(requestId: string): Promise<void> {
    this.acknowledging.update((ids) => new Set(ids).add(requestId));
    try {
      await this.pendingRequestsService.resolve(requestId);
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not mark that done.', 'Dismiss', { duration: 4000 });
    } finally {
      this.acknowledging.update((ids) => {
        const next = new Set(ids);
        next.delete(requestId);
        return next;
      });
    }
  }

  /**
   * The action on a "Water" request row — adds the bottle to the table's
   * order (so it lands on the bill) and acknowledges the request in one tap.
   * Falls back to a plain acknowledge, with a nudge, when the venue has no
   * water item on its menu.
   */
  /** Item 11 — same manage_orders gate as RequestAlerts.canAddWater; addWater ultimately calls createStaffOrder, which the server refuses without it. */
  readonly canAddWater = computed(() => this.tenancy.permissions().includes('manage_orders'));

  readonly canSetPin = computed(() => this.permissions.can('manage_rewards'));

  /** Rewards-PIN reset — opens the Set PIN dialog, which acknowledges + resolves the request once the PIN is set. */
  openSetPin(request: GuestRequest): void {
    this.dialog.open<boolean, RewardPinResetDialogData>(RewardPinResetDialog, {
      width: '360px',
      data: {
        requestId: request.id,
        tableLabel: this.tableLabel(request.tableId),
        guestName: request.guestName || null,
        guestPhone: request.guestPhone,
      },
    });
    this.closed.emit();
  }

  async addWater(request: GuestRequest): Promise<void> {
    this.acknowledging.update((ids) => new Set(ids).add(request.id));
    try {
      const outcome = await this.pendingRequestsService.fulfilWaterRequest(request);
      const message =
        outcome === 'ordered'
          ? `Water added to ${this.tableLabel(request.tableId)}'s order.`
          : outcome === 'queued'
            ? `You're offline — the water order for ${this.tableLabel(request.tableId)} will send once you're back online.`
            : 'Acknowledged. Add a water item to your menu so it can be billed automatically.';
      this.toasts.open(message, 'Dismiss', { duration: 5000 });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      const message =
        code === 'permission-denied' || code === 'functions/permission-denied'
          ? "You don't have permission to add orders — ask a manager to add the water."
          : err instanceof Error
            ? err.message
            : 'Could not add water to the order.';
      this.toasts.open(message, 'Dismiss', { duration: 4000 });
    } finally {
      this.acknowledging.update((ids) => {
        const next = new Set(ids);
        next.delete(request.id);
        return next;
      });
    }
  }

  viewAllRequests(): void {
    void this.router.navigate(['/tables'], { queryParams: { view: 'requests' } });
    this.closed.emit();
  }

  viewAllOrders(): void {
    void this.router.navigate(['/orders'], { queryParams: { view: 'active' } });
    this.closed.emit();
  }

  viewBilling(): void {
    void this.router.navigate(['/billing'], { queryParams: { view: 'today' } });
    this.closed.emit();
  }

  openScreenshot(url: string): void {
    window.open(url, '_blank', 'noopener');
  }
}
